import React, { useEffect, useState } from "react";
import {
	Avatar,
	Button,
	CssBaseline,
	TextField,
	FormControlLabel,
	Checkbox,
	Link,
	Paper,
	Box,
	Grid,
	Typography,
	Container,
	Fade,
	useTheme
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link as RouterLink } from "react-router-dom";
import _ from "@lodash";
import jwtService from "../../../authentication/services/jwtService";
import { DialogModal } from "@components";
import { COLORS } from "@constants";
import packageInfo from "../../../../package.json";
import { ImageSet, SystemConfig } from "@setting";

function Copyright(props) {
	return (
		<Box sx={{ mt: 4, mb: 2 }}>
			<Typography variant="body2" color="text.secondary" align="center" {...props}>
				{`Copyright © ${SystemConfig?.Name} v${packageInfo.version}`}
			</Typography>
			<Typography variant="body2" color="text.secondary" align="center">
				{`${packageInfo.company} ${new Date().getFullYear()}`}
			</Typography>
		</Box>
	);
}

const schema = yup.object().shape({
	email: yup.string().required("You must enter an email/login ID"),
	password: yup
		.string()
		.required("Please enter your password.")
		.min(8, "Password is too short - must be at least 8 chars."),
});

const defaultValues = {
	email: "",
	password: "",
	remember: false,
};

export default function SignInSide() {
	const theme = useTheme();
	const { control, formState, setValue, handleSubmit } = useForm({
		mode: "onChange",
		defaultValues,
		resolver: yupResolver(schema),
	});
	
	const account = JSON.parse(localStorage.getItem("rememberMeAccount"));
	const password = JSON.parse(localStorage.getItem("rememberMePassword"));
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [errorMsg, setErrorMsg] = useState("");

	const { isValid, dirtyFields, errors } = formState;

	useEffect(() => {
		const rememberCheck = account ? true : false;
		if (account) setValue("email", account, { shouldDirty: true, shouldValidate: true });
		if (password) setValue("password", password, { shouldDirty: true, shouldValidate: true });
		setValue("remember", rememberCheck, { shouldDirty: true, shouldValidate: false });
	}, [setValue, account, password]);

	function onSubmit({ email, password, remember }) {
		setErrorMsg("");
		jwtService
			.signInWithEmailAndPassword(email, password, true)
			.then((user) => {
				if (remember) {
					localStorage.setItem("rememberMeAccount", JSON.stringify(email));
					localStorage.setItem("rememberMePassword", JSON.stringify(password));
				} else {
					localStorage.removeItem("rememberMeAccount");
					localStorage.removeItem("rememberMePassword");
				}
			})
			.catch((_errors) => {
				const errorMessages = Array.isArray(_errors) ? _errors.map((error, i) => (
					<React.Fragment key={i}>
						{error.message}
						<br />
					</React.Fragment>
				)) : "Login failed. Please check your credentials.";
				setErrorMsg(errorMessages);
				setIsDialogOpen(true);
			});
	}

	return (
		<Grid container component="main" sx={{ height: "100vh", overflow: "hidden" }}>
			<CssBaseline />
			{isDialogOpen && (
				<DialogModal
					message={errorMsg}
					handleModalStats={() => setIsDialogOpen(false)}
					isDialogOpen={isDialogOpen}
					icon={{ icon: "bxs:error-alt", color: COLORS.red }}
					customHeight={"250px"}
				/>
			)}
			
			{/* Left Side: Image with Overlay */}
			<Grid
				item
				xs={false}
				sm={4}
				md={7}
				sx={{
					position: 'relative',
					backgroundImage: `url(${ImageSet.Background})`,
					backgroundRepeat: "no-repeat",
					backgroundSize: "cover",
					backgroundPosition: "center",
					"&::after": {
						content: '""',
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						background: 'linear-gradient(to right, rgba(0,0,0,0.4), rgba(0,0,0,0.1))',
					}
				}}
			>
 

			</Grid>

			{/* Right Side: Login Form */}
			<Grid
				item
				xs={12}
				sm={8}
				md={5}
				component={Paper}
				elevation={0}
				square
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					bgcolor: '#f8fafc'
				}}
			>
				<Fade in timeout={800}>
					<Container sx={{ maxWidth: '550px !important' }}>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								p: { xs: 4, md: 8 },
								bgcolor: 'white',
								borderRadius: 3,
								boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
							}}
						>
							<Avatar
								sx={{ 
									width: 100, 
									height: 100, 
									mb: 3, 
									bgcolor: 'transparent',
									border: '2px solid' + theme.palette.primary.light
								}}
								src={ImageSet.AppIcon}
							/>
							<Typography component="h1" variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
								Welcome Back
							</Typography>
							<Typography variant="h6" color="text.secondary" sx={{ mb: 6, fontWeight: 400, opacity: 0.7 }}>
								Please enter your details to sign in
							</Typography>

							<Box component="form" noValidate onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
								<Controller
									name="email"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											margin="normal"
											required
											fullWidth
											id="email"
											label="Email or Login ID"
											error={!!errors.email}
											helperText={errors?.email?.message}
											sx={{ mb: 2 }}
										/>
									)}
								/>
								<Controller
									name="password"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											margin="normal"
											required
											fullWidth
											label="Password"
											type="password"
											id="password"
											error={!!errors.password}
											helperText={errors?.password?.message}
										/>
									)}
								/>
								
								<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, mb: 2 }}>
									<FormControlLabel
										control={
											<Controller
												name="remember"
												control={control}
												render={({ field }) => (
													<Checkbox
														{...field}
														checked={field.value}
														color="primary"
													/>
												)}
											/>
										}
										label={<Typography variant="body2">Remember me</Typography>}
									/>
									<Link
										component={RouterLink}
										to="/forgot-password"
										variant="body2"
										sx={{ fontWeight: 600, textDecoration: 'none' }}
									>
										Forgot password?
									</Link>
								</Box>

								<Button
									type="submit"
									fullWidth
									disabled={_.isEmpty(dirtyFields) || !isValid}
									variant="contained"
									size="large"
									sx={{ 
										py: 1.5, 
										fontSize: '1rem',
										boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
									}}
								>
									Sign In
								</Button>

								<Copyright />
							</Box>
						</Box>
					</Container>
				</Fade>
			</Grid>
		</Grid>
	);
}
